import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { BACKEND_URL } from '../../config';

import {
  ArrowLeft,
  Buildings,
  CaretDown,
  Check,
  CheckCircle,
  Clock,
  ClockCounterClockwise,
  Crown,
  FloppyDisk,
  GearSix,
  GitBranch,
  Lightning,
  Plus,
  Rocket,
  SignOut,
  WarningCircle,
  PaperPlaneTilt,
  Storefront,
  Eye,
  XCircle
} from '@phosphor-icons/react';
// Helper to clean org names (remove email artifacts)
const cleanOrgName = (name) => {
  if (!name) return '';
  // Remove common email suffixes that might appear
  return name.replace(/@.*$/, '').replace(/['"]s?\s*org.*$/i, "'s Organization");
};

/**
 * BuilderHeader - Top navigation bar for the Unified Agent Builder
 */
const BuilderHeader = ({ 
  title, 
  status = 'draft',
  onBack, 
  onSave, 
  onDeploy,
  onEditWithCommand,
  onSubmitToMarketplace,
  marketplaceStatus,
  saving = false,
  showVersionHistory = false,
  onToggleVersionHistory,
  versionHistoryActive = false,
  currentVersion = null,
  hasUnsavedChanges = false,
  lastAutoSave = null
}) => {
  const navigate = useNavigate();
  const { logout, user: authUser } = useAuth();
  const { activeOrganization, organizations, setActiveOrganization } = useOrganization();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [user, setUser] = useState(null);
  const profileRef = useRef(null);

  // Determine user tier
  const tier = user?.plan || user?.tier || authUser?.plan || 'free';
  const isAdmin = user?.role === 'admin' || user?.is_admin || authUser?.role === 'admin';

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data);
        }
      } catch (error) {
        console.error('Failed to fetch user:', error);
      }
    };
    fetchUser();
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const statusConfig = {
    draft: { icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/20', label: 'Draft' },
    active: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/20', label: 'Active' },
    error: { icon: WarningCircle, color: 'text-red-400', bg: 'bg-red-500/20', label: 'Error' }
  };

  const mktStatusConfig = {
    draft: { icon: FloppyDisk, label: 'Submission Draft', color: 'text-gray-400', bg: 'bg-gray-500/15' },
    submitted: { icon: PaperPlaneTilt, label: 'Submitted', color: 'text-blue-400', bg: 'bg-blue-500/15' },
    in_review: { icon: Eye, label: 'In Review', color: 'text-amber-400', bg: 'bg-amber-500/15' },
    approved: { icon: CheckCircle, label: 'Approved', color: 'text-emerald-400', bg: 'bg-emerald-500/15' },
    published: { icon: CheckCircle, label: 'Published', color: 'text-green-400', bg: 'bg-green-500/15' },
    rejected: { icon: XCircle, label: 'Rejected', color: 'text-red-400', bg: 'bg-red-500/15' },
    unpublished: { icon: Clock, label: 'Unpublished', color: 'text-gray-400', bg: 'bg-gray-500/15' },
  };

  const mktCfg = marketplaceStatus ? mktStatusConfig[marketplaceStatus] : null;
  const MktIcon = mktCfg?.icon;
  
  const currentStatus = statusConfig[status] || statusConfig.draft;
  const StatusIcon = currentStatus.icon;

  return (
    <header className="h-16 bg-[#12131a] border-b border-white/10 flex items-center justify-between px-4">
      {/* Left - Back + Title */}
      <div className="flex items-center gap-4">
        {onBack && (
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/5 text-slate-400 hover:text-white transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
          </button>
        )}
        
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold text-white flex items-center gap-2">
            {title || 'New Agent'}
            {hasUnsavedChanges && (
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" title="Unsaved changes" />
            )}
          </h1>
          
          {status && (
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${currentStatus.bg}`}>
              <StatusIcon size={12} className={currentStatus.color} />
              <span className={`text-xs font-medium ${currentStatus.color}`}>
                {currentStatus.label}
              </span>
            </div>
          )}

          {/* Version Badge */}
          {currentVersion && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-500/20 text-slate-400">
              <GitBranch size={12} />
              <span className="text-xs font-medium">
                v{currentVersion.version_number}
              </span>
            </div>
          )}
        </div>
      </div>
      
      {/* Right - Actions */}
      <div className="flex items-center gap-2">
        {/* Auto-save indicator */}
        {lastAutoSave && !hasUnsavedChanges && (
          <span className="text-[11px] text-emerald-400/60 flex items-center gap-1 mr-1">
            <CheckCircle size={11} /> Saved
          </span>
        )}
        {hasUnsavedChanges && (
          <span className="text-[11px] text-amber-400/70 mr-1">Unsaved</span>
        )}

        {/* Secondary actions group */}
        <div className="flex items-center gap-1 p-0.5 bg-white/[0.03] rounded-xl border border-white/[0.06]">
          {/* Edit with Command */}
          {onEditWithCommand && (
            <button
              onClick={onEditWithCommand}
              className="flex items-center gap-1.5 px-3 py-[7px] rounded-[10px] text-[13px] font-medium transition-all
                text-slate-400 hover:text-indigo-300 hover:bg-indigo-500/10"
              data-testid="edit-with-command-btn"
              title="Edit this agent with AI using Command"
            >
              <Lightning size={14} weight="bold" />
              <span className="hidden xl:inline">AI Assist</span>
            </button>
          )}

          {/* Version History Toggle */}
          {showVersionHistory && onToggleVersionHistory && (
            <button
              onClick={onToggleVersionHistory}
              className={`flex items-center gap-1.5 px-3 py-[7px] rounded-[10px] text-[13px] font-medium transition-all ${
                versionHistoryActive 
                  ? 'bg-indigo-500/15 text-indigo-400' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid="toggle-version-history-btn"
            >
              <ClockCounterClockwise size={14} />
              <span className="hidden xl:inline">History</span>
            </button>
          )}

          {/* Marketplace Status */}
          {onSubmitToMarketplace && (
            <button
              onClick={onSubmitToMarketplace}
              className={`flex items-center gap-1.5 px-3 py-[7px] rounded-[10px] text-[13px] font-medium transition-all ${
                mktCfg 
                  ? `${mktCfg.color} hover:bg-white/5` 
                  : 'text-slate-400 hover:text-amber-300 hover:bg-amber-500/10'
              }`}
              data-testid={mktCfg ? "marketplace-status-btn" : "submit-marketplace-btn"}
              title={mktCfg ? `Marketplace: ${mktCfg.label}` : 'Submit to Marketplace'}
            >
              {mktCfg ? <MktIcon size={14} /> : <Storefront size={14} />}
              <span className="hidden xl:inline">{mktCfg ? mktCfg.label : 'Publish'}</span>
            </button>
          )}
        </div>

        {/* Primary actions */}
        {onSave && (
          <button
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-[7px] rounded-xl text-[13px] font-medium transition-all
              bg-white/[0.06] hover:bg-white/10 border border-white/[0.08]
              text-slate-200 hover:text-white
              disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="save-draft-btn"
          >
            <FloppyDisk size={14} />
            {saving ? 'Saving...' : 'Save'}
          </button>
        )}
        
        {onDeploy && (
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onDeploy}
            disabled={saving}
            className="flex items-center gap-1.5 px-5 py-[7px] rounded-xl text-[13px] font-semibold transition-all
              bg-gradient-to-r from-indigo-500 to-violet-500
              hover:from-indigo-400 hover:to-violet-400
              text-white shadow-lg shadow-indigo-500/20
              disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="deploy-agent-btn"
          >
            <Rocket size={14} weight="fill" />
            Deploy
          </motion.button>
        )}

        {/* Divider */}
        <div className="w-px h-7 bg-white/8 mx-0.5" />

        {/* Profile Menu */}
        <div className="relative" ref={profileRef}>
          <button
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="profile-menu-trigger"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center">
              <span className="text-white text-sm font-bold">
                {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <CaretDown size={16} className={`text-white transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
          </button>

          <AnimatePresence>
            {showProfileMenu && (
              <>
                <div 
                  className="fixed inset-0 z-[90]"
                  onClick={() => setShowProfileMenu(false)}
                />
                <motion.div
                  initial={{ opacity: 0, y: 8, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 8, scale: 0.95 }}
                  className="absolute right-0 mt-2 w-64 bg-[#1a1a2e]/95 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl overflow-hidden z-[100]"
                >
                  {/* User Info Header */}
                  <div className="p-4 border-b border-white/10">
                    <p className="text-white font-semibold truncate">{cleanOrgName(user?.name) || user?.email || 'User'}</p>
                    <p className="text-[#A0AEC0] text-sm truncate">{cleanOrgName(activeOrganization?.name) || 'No organization'}</p>
                  </div>
                  
                  {/* Organization Switcher - Multi-tenant */}
                  {(tier === 'partner_pro' || tier === 'partner-pro') && organizations && organizations.length > 0 && (
                    <div className="border-b border-white/10">
                      <div className="px-4 py-3">
                        <p className="text-xs text-[#A0AEC0] uppercase tracking-wide mb-2">Organizations</p>
                        
                        {/* List of organizations */}
                        <div className="space-y-1 mb-2">
                          {organizations.map((org) => (
                            <button
                              key={org.organization_id}
                              onClick={() => {
                                if (org.organization_id !== activeOrganization?.organization_id) {
                                  setActiveOrganization(org.organization_id);
                                }
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded transition-all ${
                                org.organization_id === activeOrganization?.organization_id
                                  ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white'
                                  : 'text-[#A0AEC0] hover:bg-white/10 hover:text-white'
                              }`}
                            >
                              <Buildings size={16} className="flex-shrink-0" />
                              <span className="truncate flex-1 text-left">{cleanOrgName(org.name)}</span>
                              {org.organization_id === activeOrganization?.organization_id && (
                                <Check size={16} className="flex-shrink-0" />
                              )}
                            </button>
                          ))}
                        </div>
                        
                        {/* Create New Organization button - Only for admins */}
                        {isAdmin && (
                          <button
                            onClick={() => {
                              setShowProfileMenu(false);
                              navigate('/partner-pro/organization');
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#14F195] hover:bg-[rgba(20,241,149,0.1)] transition-all rounded border border-[rgba(20,241,149,0.3)]"
                          >
                            <Plus size={16} />
                            <span>Create Organization</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Account Settings */}
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate('/account');
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-[#A0AEC0] hover:bg-white/10 hover:text-white transition-all"
                  >
                    <GearSix size={18} />
                    <span className="text-sm">Account Settings</span>
                  </button>
                  
                  {/* Billing - Only for admins */}
                  {isAdmin && (
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        navigate('/billing');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-[#A0AEC0] hover:bg-white/10 hover:text-white transition-all"
                    >
                      <Crown size={18} />
                      <span className="text-sm">Billing</span>
                    </button>
                  )}
                  
                  {/* SignOut */}
                  <button
                    onClick={() => {
                      setShowProfileMenu(false);
                      handleLogout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-[#EF4444] hover:bg-white/10 transition-all border-t border-white/10"
                  >
                    <SignOut size={18} />
                    <span className="text-sm">Sign Out</span>
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default BuilderHeader;
